"use client";

import { normalizeDateKey } from "@/lib/utils/dates";
import { useState, useMemo } from "react";
import Calendar from "react-calendar";
import { useLocale } from "next-intl";

export interface DatePickerProps {
  value?: Date;
  activeStartDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Array<Date | string>;
  onMonthViewChange: (date: Date) => void;
  onSelect: (date: Date) => void;
}

export function DatePicker({
  value,
  activeStartDate,
  minDate,
  maxDate,
  disabledDates,
  onMonthViewChange,
  onSelect,
}: Readonly<DatePickerProps>) {
  const initialDate = value ? new Date(value) : new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);

  const disabledSet = useMemo(() => {
    return new Set(
      disabledDates?.map((x) => {
        const d = new Date(x);
        return normalizeDateKey(d);
      }) || [],
    );
  }, [disabledDates]);

  const handleViewChange = ({
    activeStartDate,
  }: {
    activeStartDate: Date | null;
  }) => {
    if (!activeStartDate) return;

    onMonthViewChange(
      new Date(activeStartDate.getFullYear(), activeStartDate.getMonth(), 1),
    );
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onSelect?.(date);
  };

  return (
    <Calendar
      value={selectedDate}
      activeStartDate={activeStartDate}
      minDate={minDate ? new Date(minDate) : undefined}
      maxDate={maxDate ? new Date(maxDate) : undefined}
      showNeighboringMonth={false}
      showNavigation={false}
      onActiveStartDateChange={handleViewChange}
      onChange={(d) => {
        handleDateSelect(d as Date);
      }}
      tileDisabled={({ date }) => disabledSet.has(normalizeDateKey(date))}
      locale={useLocale()}
    />
  );
}
